import { StaticDataObject } from "context/staticData/staticData";
import { Column } from "react-table";
import ReimbursementStatus from "shared/enums/ReimbursementStatus";
import { ApiParticipantReimbursementAdminForListing } from "shared/types/ApiParticipantReimbursementAdminForListing";
import CellEllipsis from "shared/core/Table/components/cellEllipsis";
import { ProgramType } from "shared/enums/ProgramType";
import SelectableType from "shared/enums/SelectableType";
import SelectionType from "shared/enums/SelectionType";
import { StateStatus } from "shared/enums/StateStatus";
import SchemaService from "shared/form/renderer/SchemaService";
import { Copies } from "shared/hooks/useCopies";
import { ApiParticipantForSelectionForAdminResponse } from "shared/types/ApiParticipantForSelectionForAdminResponse";
import { ApiProgramResponse } from "shared/types/ApiProgramResponse";
import { getTableColumnsForReimbursement } from "../../reimbursement/list/utils";
import I18n from "shared/types/I18n";
import { ApiFeaturedListResponse } from "shared/types/selection/ApiSelectionFeaturedList";
import { AdminReimbursmentAction } from "./types";
import { FeaturedListContextType } from "shared/enums/FeaturedListContextType";
import FeaturedListType from "shared/enums/FeaturedListType";
import InventoryType from "shared/enums/InventoryType";
import { ApiDateTime } from "shared/types/ApiDateTime";

export default abstract class BaseProgram {
  protected programDetails: ApiProgramResponse | null;
  constructor(programDetails: ApiProgramResponse | null) {
    this.programDetails = programDetails;
  }
  abstract canShowAdditionalMeetingMatching(): boolean;
  abstract canShowNoShowFilter(): boolean;
  abstract canAdminMarkAsNoShow(): boolean;

  canConvertToEmailIntroduction(): boolean {
    return false;
  }

  canShowMeetingRepAssignmentFilters(): boolean {
    return false;
  }

  checkProgramState(state: string, status: StateStatus): boolean {
    if (this.programDetails) {
      return this.programDetails.timeline.some(item => item.state === state && item.stateStatus === status);
    }
    return false;
  }

  tableColumnsForReimbursement(
    copies: Copies,
    staticDataObj: StaticDataObject
  ): Array<Column<ApiParticipantReimbursementAdminForListing>> {
    return getTableColumnsForReimbursement({ copies, staticDataObj });
  }

  getNextReimbursementState(currentState?: ReimbursementStatus): Array<AdminReimbursmentAction> {
    switch (currentState) {
      case ReimbursementStatus.PENDING_APPROVAL:
        return [
          { actionType: ReimbursementStatus.APPROVED, positive: true, key: "console.reimbursement.approve" },
          { actionType: ReimbursementStatus.REJECTED, positive: false, key: "console.reimbursement.reject" }
        ];
      case ReimbursementStatus.APPROVED:
        return [
          { actionType: ReimbursementStatus.PROCESSED, positive: true, key: "console.reimbursement.mark.processed" }
        ];
      default:
        return [];
    }
  }

  getMaximunReimbursementAmount(claimedAmount?: number): number {
    if (this.programDetails) {
      return this.programDetails.reimbursementAllowance?.value || 0;
    }
    return 0;
  }
  isFSLConfigurationEditDisabled(response: ApiFeaturedListResponse | undefined): boolean {
    return !!response && response?.type === FeaturedListType.MASTER;
  }

  getProgramType(): ProgramType | undefined {
    return this.programDetails?.programType;
  }
  isAiRecommendationEnabled(): boolean {
    if (this.programDetails) {
      return this.programDetails.aiSelectionRecommendationEnabled;
    }
    return false;
  }

  getFSLTypeOptions(
    staticData: StaticDataObject,
    isCreateMode: boolean,
    contextType: FeaturedListContextType,
    selectionType?: SelectionType
  ) {
    let options = SchemaService.getOptionsListFromStaticData(staticData.selectionFeaturedListType);
    if (contextType === FeaturedListContextType.EXHIBITOR_LIST) {
      const hasToRemoveMaster = isCreateMode;
      if (hasToRemoveMaster) {
        options = options.filter(item => item.value !== FeaturedListType.MASTER);
      }
      return options;
    }

    const programType = this.getProgramType();
    if (programType) {
      const hasToRemoveMaster = isCreateMode;
      if (hasToRemoveMaster) {
        options = options.filter(item => item.value !== FeaturedListType.MASTER);
      }

      const hasToRemoveDefault = !staticData.defaultFSLConfigurableForProgramTypes.includes(programType);

      if (hasToRemoveDefault) {
        options = options.filter(item => item.value !== FeaturedListType.DEFAULT);
      }

      const hasToRemoveCustom = selectionType !== SelectionType.REQUEST;
      if (hasToRemoveCustom) {
        options = options.filter(item => item.value !== FeaturedListType.CUSTOM);
      }
    }
    return options;
  }

  /**
   * This is a sample function that takes two numbers and adds them together.
   * @param {firstState} firstState First State is ON_GOING or ELLAPSED.
   * @param {secondState} secondState Second State is ON_GOING or NOT_YET_TIME.
   * @returns {boolean} The sum of the two numbers.
   */
  isProgramStateInBetween({ firstState, secondState }: { firstState: string; secondState: string }): boolean {
    if (this.programDetails) {
      const isFirstStateOnGoingOrEllapsed =
        this.checkProgramState(firstState, StateStatus.ON_GOING) ||
        this.checkProgramState(firstState, StateStatus.ELAPSED);

      const isSecondStateOnGoingOrNotYetTime =
        this.checkProgramState(secondState, StateStatus.ON_GOING) ||
        this.checkProgramState(secondState, StateStatus.NOT_YET_TIME);

      return isFirstStateOnGoingOrEllapsed && isSecondStateOnGoingOrNotYetTime;
    }
    return false;
  }

  getSwitcherConfigInSelectionRound2(): {
    isAvailable: boolean;
    defaultOption: SelectionType | null;
    screenName: keyof I18n;
  } {
    return {
      isAvailable: true,
      defaultOption: null,
      screenName: ""
    };
  }
  getCommonColumnsForSelectionRounds(
    copies: Copies,
    staticDataObj: StaticDataObject,
    selectableType: SelectableType
  ): Array<Column<ApiParticipantForSelectionForAdminResponse>> {
    if (selectableType === SelectableType.ORGANIZATION) {
      return [
        {
          id: "name",
          Header: copies.get("organization.name"),
          accessor: "organizationName",
          width: 180,
          Cell: CellEllipsis
        }
      ];
    }

    return [
      {
        id: "name",
        Header: copies.get("name"),
        accessor: ({ firstName, lastName }) => `${firstName} ${lastName}`,
        width: 180,
        Cell: CellEllipsis
      },
      {
        id: "company",
        Header: copies.get("organization.name"),
        accessor: "organizationName",
        width: 180,
        Cell: CellEllipsis
      },
      {
        id: "applicableTag",
        Header: copies.get("tags"),
        accessor: d => staticDataObj.programParticipantTag?.[d.applicableTag],
        width: 180,
        sortType: "basic",
        Cell: CellEllipsis
      },
      {
        id: "time-slots",
        Header: copies.get("timeslot.selected.count"),
        accessor: "optedInTimeSlotCount",
        width: 180,
        sortType: "basic",
        Cell: CellEllipsis
      }
    ];
  }

  getTableColumnsInListForSelectionRoundOne(
    copies: Copies,
    staticDataObj: StaticDataObject,
    selectableType: SelectableType
  ): Column<ApiParticipantForSelectionForAdminResponse>[] {
    return [];
  }
  getTableColumnsInListForSelectionRoundTwo(
    copies: Copies,
    staticDataObj: StaticDataObject,
    selectionType: SelectionType | null,
    selectableType: SelectableType
  ): Column<ApiParticipantForSelectionForAdminResponse>[] {
    return [];
  }
  getTableColumnsInListForSelectionRoundThree(
    copies: Copies,
    staticDataObj: StaticDataObject,
    selectableType: SelectableType
  ): Column<ApiParticipantForSelectionForAdminResponse>[] {
    return [];
  }
  isUnscheduledMeetingsPurchaseEnabled(): boolean {
    if (this.programDetails) {
      return this.programDetails.unscheduledMeetingsPurchaseEnabled;
    }
    return false;
  }

  getInvitesSentStateChangeDate(): ApiDateTime | null {
    return null;
  }

  abstract canAddSponsorship(): boolean;
  abstract canManageMeetingCap(existingMeetingCap?: number): boolean;
  abstract canViewMeetingPurchases(): boolean;
  abstract canRemoveOrgFromProgram(): boolean;
  abstract canSendNotification(): boolean;
  isUncappedMeetingConfigured(): boolean {
    return Boolean(this.programDetails?.meetingInventoryTypes.includes(InventoryType.BUNDLED_MEETING));
  }
}
